/**
 * PresentationLibraryPage — the Presentation Library (#5, migration 0097).
 *
 * A firm-wide shelf of company-approved sales / marketing material (decks, PDFs,
 * one-pagers, videos). Browse + download is open to every authenticated user (a
 * primary nav destination); upload + remove is gated on the `presentation.manage`
 * permission (seeded to admin). Files are stored inline in the DB (0095 blob
 * pattern) — they travel with the nightly backup, no external object store.
 *
 * Upload is a Pattern B immediate-commit action (§3g): the Add button IS the
 * commit gesture, its own mini-form, not a page-level Edit transaction. Remove is
 * a per-row action behind a confirm modal (irreversible-from-the-user's-view).
 */
import { useState } from 'react';
import {
  ActionIcon, Alert, Button, Card, Container, FileInput, Group, Loader, Modal,
  Stack, Table, Text, TextInput, Textarea, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconFile, IconTrash, IconUpload } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { presentationsApi, type PresentationFile } from '../api/presentations';
import { BackToCompliance } from '../components/BackToCompliance';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePermissions } from '../hooks/usePermissions';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return '—'; }
}

const QK = ['presentations', 'list'] as const;

function UploadCard({ onUploaded }: { onUploaded: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: () => presentationsApi.upload({ title: title.trim(), description, file: file! }),
    onSuccess: (rec) => {
      notifications.show({ color: 'green', title: 'File added', message: rec.title });
      setTitle(''); setDescription(''); setFile(null);
      onUploaded();
    },
    onError: (e) => notifications.show({ color: 'red', title: 'Upload failed', message: errMsg(e) }),
  });

  // A title defaults to the filename server-side, so only a file is strictly required.
  const canSubmit = !!file && !upload.isPending;

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="sm">
        <Group gap="xs">
          <IconUpload size={18} />
          <Title order={5}>Add a file</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Upload a company-approved presentation or marketing file (any type, up to 50 MB).
          Everyone can browse and download what you add here.
        </Text>
        <TextInput
          label="Title"
          placeholder="Defaults to the file name if left blank"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <Textarea
          label="Description"
          placeholder="Optional — a short note on what this is / when to use it"
          autosize
          minRows={1}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <FileInput
          label="File"
          placeholder="Choose a file"
          leftSection={<IconFile size={16} />}
          value={file}
          onChange={setFile}
          clearable
          withAsterisk
        />
        <Group justify="flex-end">
          <Button
            leftSection={<IconUpload size={16} />}
            onClick={() => upload.mutate()}
            loading={upload.isPending}
            disabled={!canSubmit}
          >
            Add to library
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export function PresentationLibraryPage() {
  useDocumentTitle('Presentation Library');
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canManage = can('presentation.manage');

  const [downloading, setDownloading] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PresentationFile | null>(null);

  const listQ = useQuery({ queryKey: QK, queryFn: () => presentationsApi.list() });

  const refresh = () => { void qc.invalidateQueries({ queryKey: QK }); };

  const remove = useMutation({
    mutationFn: (id: string) => presentationsApi.remove(id),
    onSuccess: () => {
      notifications.show({ color: 'green', title: 'Removed', message: deleteTarget?.title ?? 'File removed.' });
      setDeleteTarget(null);
      refresh();
    },
    onError: (e) => notifications.show({ color: 'red', title: 'Could not remove', message: errMsg(e) }),
  });

  async function handleDownload(f: PresentationFile) {
    setDownloading(f.id);
    try {
      await presentationsApi.download(f.id, f.filename);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Download failed', message: errMsg(e) });
    } finally {
      setDownloading(null);
    }
  }

  const files = listQ.data ?? [];

  return (
    <Container size="xl" pt="md">
      <Stack>
        <BackToCompliance to="/" label="Back to Welcome" />
        <div>
          <Title order={3}>Presentation Library</Title>
          <Text size="sm" c="dimmed">
            Company-approved sales and marketing material, available to everyone.
          </Text>
        </div>

        {canManage && <UploadCard onUploaded={refresh} />}

        <Card withBorder padding="lg" radius="md">
          {listQ.isLoading && <Loader />}
          {listQ.isError && <Alert color="red" title="Could not load the library">{errMsg(listQ.error)}</Alert>}
          {listQ.isSuccess && files.length === 0 && (
            <Text size="sm" c="dimmed">
              No files yet.{canManage ? ' Use “Add a file” above to upload the first one.' : ''}
            </Text>
          )}
          {listQ.isSuccess && files.length > 0 && (
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>File</Table.Th>
                  <Table.Th>Size</Table.Th>
                  <Table.Th>Added</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {files.map((f) => (
                  <Table.Tr key={f.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{f.title}</Text>
                      {f.description && <Text size="xs" c="dimmed">{f.description}</Text>}
                      <Text size="xs" c="dimmed">{f.filename}</Text>
                    </Table.Td>
                    <Table.Td><Text size="sm">{fmtSize(f.size_bytes)}</Text></Table.Td>
                    <Table.Td><Text size="sm">{fmtDate(f.created_at)}</Text></Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconDownload size={14} />}
                          onClick={() => handleDownload(f)}
                          loading={downloading === f.id}
                        >
                          Download
                        </Button>
                        {canManage && (
                          <Tooltip label="Remove from library">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => setDeleteTarget(f)}
                              aria-label="Remove file"
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal
        opened={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Remove from library"
      >
        <Stack>
          <Text size="sm">
            Remove <b>{deleteTarget?.title}</b> from the Presentation Library? It will no longer be
            available to download.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              color="red"
              loading={remove.isPending}
              onClick={() => { if (deleteTarget) remove.mutate(deleteTarget.id); }}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
